/*
 *  Copyright
 *
 *  Tegona S.A.
 *
 *  Tegona JS © 2017, Tegona S.A.
 *
 *  ALL RIGHTS RESERVED. THIS PROGRAM CONTAINS MATERIAL PROTECTED
 *  UNDER INTERNATIONAL AND SWITZERLAND COPYRIGHT LAWS AND TREATIES.
 *  ANY UNAUTHORIZED USE OF THE PROGRAM, INCLUDING REPRODUCTION,
 *  MODIFICATION, TRANSFER, TRANSMITTAL OR REPUBLICATION OF THIS
 *  MATERIAL IN ANY FORM OR BY ANY MEANS IS PROHIBITED WITHOUT
 *  SPECIFIC WRITTEN PERMISSION OF THE COPYRIGHT HOLDER.
 *
 *  copyright@tegona.com
*/

import React, { useDebugValue } from 'react'
import autoBind from 'react-autobind'
import R from 'ramda'
import moment from 'moment'
import { bindActionCreators } from 'redux'
import { withRouter } from 'react-router-dom'
import { injectIntl } from 'react-intl'
import { connect } from 'react-redux'
import { Card, Subheader, MenuItem, FontIcon, RaisedButton } from 'material-ui'
import { red500, grey400 } from 'material-ui/styles/colors'

import { AutoComplete, TextField, SelectField } from 'generic/form'
import { selectHelper } from 'generic/form/selectHelper'

import * as constants from 'constants/Prestation'
import * as beneficiaryNumberConstants from 'constants/BeneficiaryNumber'

import * as headerActions from 'actions/Header'
import * as consumerActions from 'actions/Consumer'
import * as payingCustomerActions from 'actions/PayingCustomer'
import * as beneficiaryActions from 'actions/Beneficiary'
import * as beneficiaryNumberActions from 'actions/BeneficiaryNumber'
import * as applicantActions from 'actions/Applicant'

const getConsumerText = (consumer) => (
  R.path(['entities', 0], consumer) &&
  R.path(['identities', 0], consumer)
    ? `${consumer.identities[0].first_name} ${consumer.identities[0].last_name} ${ consumer.entities.length ? ' - ' + consumer.entities[0].name : ''}`
    : R.path(['identities', 0], consumer)
      ? `${consumer.identities[0].first_name} ${consumer.identities[0].last_name}`
      : R.path(['entities', 0], consumer)
        ? `${consumer.entities[0].name}`
        : ''
)

const getPayingCustomerText = (payingCustomer) => (
  payingCustomer &&
  payingCustomer.entities.length
    ? payingCustomer.entities[0].name
    : ''
)

const getBeneficiaryText = (beneficiary) => (
  R.path(['identities', 0], beneficiary)
    ? beneficiary.identities[0].birth_date
      ? `${beneficiary.identities[0].first_name} ${beneficiary.identities[0].last_name} - ${moment(beneficiary.identities[0].birth_date).format('DD.MM.YYYY')}`
      : `${beneficiary.identities[0].first_name} ${beneficiary.identities[0].last_name}`
    : ''
)

const getApplicantText = (applicant) => (
  R.path(['entities', 0], applicant) &&
  R.path(['identities', 0], applicant)
    ? `${applicant.identities[0].first_name} ${applicant.identities[0].last_name} - ${applicant.entities[0].name}`
    : R.path(['entities', 0], applicant)
      ? `${applicant.entities[0].name}`
      : ''
)

class PrestationInformation extends React.Component {

  constructor(props) {
    super(props)
    autoBind(this)

    this.state = {
      applicantText: getApplicantText(props.prestation.applicant),
      consumerText: getConsumerText(props.prestation.consumers[0]),
      payingCustomerText: getPayingCustomerText(props.prestation.paying_customer),
      beneficiaryText: getBeneficiaryText(props.prestation.beneficiary_numbers[0].beneficiary),
      showPayingCustomerDisclaimer: false,
      showConsumerButton: false,
      showBeneficiaryButton: false,
    }

    if (!props.prestation.beneficiary_numbers.length ||
        !props.prestation.beneficiary_numbers[0].number) {
      this.handleDefaultBeneficiaryNumberType(props.prestation.paying_customer)
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.prestation.consumers !== prevProps.prestation.consumers &&
        this.props.prestation.consumers.length) {
      this.setState({
        consumerText: getConsumerText(this.props.prestation.consumers[0])
      })
    }
  }

  handleChange(name, value) {
    this.setState({
      [name]: value
    })
  }

  handlePrestationChange(event, value) {
    this.props.handlePrestationChange(event.target.name, value)
  }

  handleRecommandedField(value) {
    return this.props.prestation.workflow_objects.length > 0 &&
           this.props.prestation.workflow_objects[0].step_name === constants.ACCEPTED_PRESTATION_STATUS &&
           !value
  }

  handleRecommandedFieldLabel(label, value) {
    if (this.handleRecommandedField(value)) {
      return [label, <span key={0} style={{color: red500}}> *</span>]
    }

    return label
  }

  handleWarningBeneficiary() {
    return (
      this.props.prestation.beneficiary_numbers.length > 0 &&
      !this.props.prestation.beneficiary_numbers[0].beneficiary &&
      this.state.beneficiaryText
    )
  }

  handleTransform(obj, name) {
    let display

    if (name === 'applicant') {
      display = getApplicantText(obj)
    } else if (name === 'consumers') {
      display = getConsumerText(obj)
    } else if (name === 'paying_customer') {
      display = getPayingCustomerText(obj)
    } else if (name === 'beneficiary_numbers') {
      display = getBeneficiaryText(obj)
    }

    return {
      ...obj,
      display: display
    }
  }

  handleApplicantSearch(searchText) {
    this.handleChange('applicantText', searchText)
  }

  async handleAutocompleteApplicant(name, value) {
    await this.props.handlePrestationChange(name, value)
    await this.props.handlePrestationChange('applicant_entity', value && value.entities.length > 0 ? value.entities[0] : undefined)

    if (value) {
      this.handleAutocompletePayingCustomer('paying_customer', value.default_paying_customer)

      this.props.handlePrestationChange('address', value.entities[0].address)
      this.props.handleFormChange('updateInterpreterSearch', true)
      this.props.handlePrestationChange('errors', {
        ...this.props.prestation.errors,
        applicant: undefined,
      })
    }
  }

  handleConsumerSearch(searchText) {
    this.setState({
      showConsumerButton: true
    })

    this.props.handleFormChange('consumerFormVisible', false)
    this.handleChange('consumerText', searchText)
    this.props.handlePrestationChange('consumers', [])
  }

  handleConsumerForm() {
    this.props.handleFormChange('consumerFormVisible', true)
  }

  handleAutocompleteConsumer(name, value) {
    this.setState({
      showConsumerButton: false
    })

    if (value) {
      const identity = {
        ...value.identities[0],
        uid: undefined
      }

      const consumer = {
        identities: [identity],
        entities: [value.entities[0]]
      }

      this.props.handlePrestationChange(name, [consumer])
      this.props.handleFormChange('consumerFormVisible', true)
    }
  }

  handlePayingCustomerSearch(searchText) {
    this.handleChange('payingCustomerText', searchText)

    this.setState({
      showPayingCustomerDisclaimer: true
    })
  }

  async handleAutocompletePayingCustomer (name, value) {
    await this.props.handlePrestationChange(name, value)
    this.props.handlePrestationChange('errors', {
      ...this.props.prestation.errors,
      paying_customer: undefined,
      beneficiary_numbers: {
        beneficiary: undefined,
        number: undefined,
        identities: {
          birth_date: undefined
        }
      }
    })

    this.handleChange('payingCustomerText', this.handleTransform(value, 'paying_customer').display)

    if (value) this.handleDefaultBeneficiaryNumberType(value)
  }

  handleBeneficiarySearch(searchText) {
    this.setState({
      showBeneficiaryButton: true
    })

    this.handleChange('beneficiaryText', searchText)

    const beneficiary_number = {
      ...this.props.prestation.beneficiary_numbers[0],
      beneficiary: undefined
    }

    this.props.handlePrestationChange('beneficiary_numbers', [beneficiary_number])
  }

  async handleAutocompleteBeneficiary(name, value) {
    this.setState({
      showBeneficiaryButton: false
    })

    if (value) {
      const identity = {
        ...value.identities[0],
        uid: undefined
      }
      const beneficiaryNumber = {
        ...this.props.prestation.beneficiary_numbers[0],
        beneficiary: {
          ...this.props.prestation.beneficiary_numbers[0].beneficiary,
          identities: [identity],
        }
      }

      await this.props.handlePrestationChange('beneficiary_numbers', [beneficiaryNumber])
      await this.props.handleFormChange('beneficiaryFormVisible', false)

      this.handleBeneficiaryNumberType('number_type', this.props.prestation.beneficiary_numbers[0].number_type)
    }
  }

  handleBeneficiaryForm() {
    this.props.handleFormChange('beneficiaryFormVisible', true)
  }

  handleSelectBeneficiaryNumberTypeChange (event, index, value, sourceKey) {
    selectHelper(value, this.props[sourceKey], this.handleBeneficiaryNumberType, event.target.name)
  }

  handleBeneficiaryNumberType(key, value) {
    const promise = new Promise((resolve, reject) => {
      const beneficiaryNumbers = {...this.props.prestation.beneficiary_numbers[0]}
      let beneficiaryNumber = beneficiaryNumbers

      if (beneficiaryNumbers.beneficiary &&
          value &&
          value.code === beneficiaryNumberConstants.BENEFICIARY_NUMBER_TYPE_CODE_CPM) {
        beneficiaryNumberActions.getBeneficiaryNumber(beneficiaryNumbers.beneficiary, value)
          .then((result) => {
            if (result.data.objects.objects.length) {
              const obj = result.data.objects.objects[0]

              beneficiaryNumber = {
                ...beneficiaryNumber,
                number: obj.number,
                number_type: obj.number_type
              }

            }

            resolve(this.props.handlePrestationChange('beneficiary_numbers',
                                                      [{...beneficiaryNumber, 'number_type': value}]))
          })
      } else {
        resolve(this.props.handlePrestationChange('beneficiary_numbers',
                                                  [{...beneficiaryNumber, 'number_type': value}]))
      }
    })

    promise
      .then(() => {
        if (value) {
          if (this.props.prestation.paying_customer.aba_code &&
              [
                beneficiaryNumberConstants.BENEFICIARY_NUMBER_TYPE_CODE_RESAMI,
                beneficiaryNumberConstants.BENEFICIARY_NUMBER_TYPE_CODE_IPP,
              ].includes(value.code)
            ) {

            this.props.handlePrestationChange('errors', {
              ...this.props.prestation.errors,
              'beneficiary_numbers.number_type': undefined,
            })
          }
        }
      })
  }

  handleDefaultBeneficiaryNumberType(value) {
    let beneficiaryNumberType
    
    if (value.default_beneficiary_number_type !== undefined) {
      beneficiaryNumberType = this.props.beneficiaryNumberTypes.filter((item) => {return item.code === value.default_beneficiary_number_type})[0]
      this.handleBeneficiaryNumberType('number_type', beneficiaryNumberType)
    } else {
      this.handleBeneficiaryNumberType('number_type', undefined)
    }
  }

  async handleBeneficiaryNumberChange(event, value) {
    const beneficiaryNumbers = {...this.props.prestation.beneficiary_numbers[0]}
    await this.props.handlePrestationChange('beneficiary_numbers', [{...beneficiaryNumbers, [event.target.name]: value || null}])

    this.props.handlePrestationChange('errors', {
      ...this.props.prestation.errors,
      'beneficiary_numbers.number': undefined
    })
  }

  handleCancel() {
    this.props.history.push({pathname: '/dashboard/'})
    this.props.headerActions.selectPage(0)
  }

  handleConfirm() {
    this.props.handleCheckConfirmation()
  }

  render () {
    const prestation = this.props.prestation
    const meta = prestation && prestation.meta ? prestation.meta.fields : {}
    const mandatory_fields = R.pathOr([], ['paying_customer', 'mandatory_fields'], prestation)

    const slip = prestation.beneficiary_numbers.length > 0 && prestation.beneficiary_numbers[0].number_type
      ? prestation.beneficiary_numbers[0].number_type.uid
      : undefined

    return (
			<Card
        style={styles.card}
      >
				<Subheader style={{textAlign: 'center'}}>
          {this.props.intl.formatMessage(constants.messages.PRESTATION_INFORMATION)} {prestation.id ? ' - ' + prestation.id : undefined}
        </Subheader>

        <div className='row'>
          <div className='col-md-12 col-xs-12'>
            {
              this.props.isStaff
                ? <AutoComplete
                  name='applicant'
                  textKey='display'
                  valueKey='uid'
                  searchKey='name'
                  floatingLabelText={this.props.intl.formatMessage(constants.messages.HISTORY_APPLICANT_LABEL)}
                  meta={meta.applicant}
                  searchText={this.state.applicantText}
                  url={applicantActions.APPLICANT_SIMPLE_LIST_URL}
                  parameters={{'is_active':true}}
                  transformData={this.handleTransform}
                  updateItem={this.handleAutocompleteApplicant}
                  onUpdateInput={this.handleApplicantSearch}
                  errorText={prestation.errors ? prestation.errors['applicant'] : undefined}
                  textFieldStyle={styles.fontText}
                  placeholderMandatory={true}
                />
                : undefined
            }
          </div>
        </div>

        <div className='row'>
          <div
            className='col-md-12 col-xs-12'
            style={{position: 'relative'}}
          >
            <AutoComplete
              name='consumers'
              textKey='display'
              valueKey='uid'
              searchKey='name'
              floatingLabelText={
                this.handleRecommandedFieldLabel(
                  this.props.intl.formatMessage(constants.messages.STEP_THREE_CONSUMER),
                  R.path(['consumers', 0, 'uid'], prestation)
                )
              }
              placeholderMandatory={mandatory_fields.includes('consumers')}
              meta={{
                'mandatory': mandatory_fields.includes('consumers')
              }}
              searchText={this.state.consumerText}
              url={consumerActions.CONSUMERS_URL}
              transformData={this.handleTransform}
              updateItem={this.handleAutocompleteConsumer}
              onUpdateInput={this.handleConsumerSearch}
              underlineStyle={
                this.handleRecommandedField(R.path(['consumers', 0, 'uid'], prestation))
                ? {borderColor: red500}
                : undefined
              }
              extraOption={
                <MenuItem
                  primaryText={this.props.intl.formatMessage(constants.messages.ADD_CONSUMER)}
                  style={{color: grey400, 'fontStyle': 'italic'}}
                />
              }
              extraAction={this.handleConsumerForm}
              errorText={
                prestation.consumers.length === 0 && this.state.consumerText
                  ? this.props.intl.formatMessage(constants.messages.STEP_THREE_FILL_CONSUMER_HINT_TEXT)
                  : R.path(['errors', 'consumers'], prestation)
              }
              textFieldStyle={styles.fontText}
            />

            {
              R.path(['consumers', 0, 'uid'], prestation)
              ? <FontIcon
                className='material-icons'
                style={{
                  margin: '20px 5px 0 0',
                  top: 15,
                  right: 0,
                  position: 'absolute',
                  cursor: 'pointer'
                }}
                onClick={this.handleConsumerForm}
              >mode_edit
              </FontIcon>
              : undefined
            }

            {
              this.state.showConsumerButton
              ? <FontIcon
                className='material-icons'
                style={{
                  margin: '20px 5px 0 0',
                  top: 15,
                  right: 0,
                  position: 'absolute',
                  cursor: 'pointer'
                }}
                onClick={this.handleConsumerForm}
              >add
              </FontIcon>
              : undefined
            }
          </div>
        </div>

        <div className='row'>
          <div className='col-md-12 col-xs-12'>
            <TextField
              meta={meta.other_contributors}
              name='other_contributors'
              value={prestation.other_contributors || undefined}
              onChange={this.handlePrestationChange}
              floatingLabelText={this.props.intl.formatMessage(constants.messages.STEP_THREE_OTHER_CONTRIBUTORS)}
              floatingLabelStyle={styles.fontText}
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-md-12 col-xs-12'>
            <AutoComplete
              name='paying_customer'
              textKey='display'
              valueKey='uid'
              searchKey='name'
              floatingLabelText={
                this.handleRecommandedFieldLabel(
                  this.props.intl.formatMessage(constants.messages.STEP_THREE_PAYING_CUSTOMER),
                  prestation.paying_customer &&
                  prestation.paying_customer.uid
                )
              }
              placeholderMandatory={true}
              searchText={this.state.payingCustomerText}
              url={payingCustomerActions.PAYING_CUSTOMERS_URL}
              transformData={this.handleTransform}
              updateItem={this.handleAutocompletePayingCustomer}
              onUpdateInput={this.handlePayingCustomerSearch}
              underlineStyle={
                this.handleRecommandedField(
                  prestation.paying_customer &&
                  prestation.paying_customer.uid
                )
                  ? {borderColor: red500}
                  : undefined
              }
              meta={{'mandatory': true}}
              textFieldStyle={styles.fontText}
              errorText={
                this.state.showPayingCustomerDisclaimer &&
                !this.state.payingCustomerText
                  ? this.props.intl.formatMessage(constants.messages.STEP_THREE_PAYING_CUSTOMER_HINT_TEXT)
                  : R.path(['errors', 'paying_customer'], prestation)
              }
            />
          </div>
        </div>

        <div className='row'>
          <div
            className='col-md-12 col-xs-12'
            style={{position: 'relative'}}
          >
            <AutoComplete
              name='beneficiary_numbers'
              textKey='display'
              valueKey='uid'
              searchKey='text'
              floatingLabelText={
                this.handleRecommandedFieldLabel(
                  this.props.intl.formatMessage(constants.messages.STEP_THREE_BENEFICIARY),
                  prestation.beneficiary_numbers &&
                  prestation.beneficiary_numbers.length > 0 &&
                  prestation.beneficiary_numbers[0].beneficiary &&
                  prestation.beneficiary_numbers[0].beneficiary.uid
                )
              }
              placeholderMandatory={mandatory_fields.includes('beneficiary')}
              meta={{
                'mandatory': mandatory_fields.includes('beneficiary')
              }}
              searchText={this.state.beneficiaryText}
              url={beneficiaryActions.BENEFICIARIES_URL}
              transformData={this.handleTransform}
              updateItem={this.handleAutocompleteBeneficiary}
              onUpdateInput={this.handleBeneficiarySearch}
              underlineStyle={
                this.handleRecommandedField(
                  prestation.beneficiary_numbers &&
                  prestation.beneficiary_numbers.length > 0 &&
                  prestation.beneficiary_numbers[0].beneficiary &&
                  prestation.beneficiary_numbers[0].beneficiary.uid
                )
                  ? {borderColor: red500}
                  : undefined
              }
              extraOption={
                <MenuItem
                  primaryText={this.props.intl.formatMessage(constants.messages.ADD_BENEFICIARY)}
                  style={{color: grey400, 'fontStyle': 'italic'}}
                />
              }
              extraAction={this.handleBeneficiaryForm}
              errorText={
                this.handleWarningBeneficiary()
                  ? this.props.intl.formatMessage(constants.messages.STEP_THREE_FILL_BENEFICIARY_HINT_TEXT)
                  : prestation.errors
                    ? prestation.errors['beneficiary_numbers.beneficiary']
                    : undefined
              }
              textFieldStyle={styles.fontText}
            />

            {
              this.props.prestation.beneficiary_numbers.length &&
              this.props.prestation.beneficiary_numbers[0].beneficiary
              ? <FontIcon
                className='material-icons'
                style={{
                  margin: '20px 5px 0 0',
                  top: 15,
                  right: 0,
                  position: 'absolute',
                  cursor: 'pointer'
                }}
                onClick={this.handleBeneficiaryForm}
              >mode_edit
              </FontIcon>
              : undefined
            }

            {
              this.state.showBeneficiaryButton
              ? <FontIcon
                className='material-icons'
                style={{
                  margin: '20px 5px 0 0',
                  top: 15,
                  right: 0,
                  position: 'absolute',
                  cursor: 'pointer'
                }}
                onClick={this.handleBeneficiaryForm}
              >add
              </FontIcon>
              : undefined
            }
          </div>
        </div>

        <div className='row'>
          <div className='col-md-6 col-xs-12'>
            <SelectField
              placeholderMandatory={
                prestation.paying_customer && prestation.paying_customer.mandatory_beneficiary_number_type != null
              }
              meta={{
                'mandatory': prestation.paying_customer && prestation.paying_customer.mandatory_beneficiary_number_type != null,
                'write': !prestation.paying_customer || prestation.paying_customer.mandatory_beneficiary_number_type == null,
              }}
              sourceKey='beneficiaryNumberTypes'
              value={
                prestation.beneficiary_numbers.length > 0 &&
                prestation.beneficiary_numbers[0].number_type
                  ? prestation.beneficiary_numbers[0].number_type.uid
                  : undefined
              }
              onChange={this.handleSelectBeneficiaryNumberTypeChange}
              floatingLabelText={this.props.intl.formatMessage(constants.messages.STEP_THREE_BENEFICIARY_NUMBER_TYPE)}
              errorText={prestation.errors ? prestation.errors['beneficiary_numbers.number_type'] : undefined}
              style={styles.fontText}
            >
              {
                this.props.beneficiaryNumberTypes.map((beneficiaryNumberType, i) => (
                  <MenuItem
                    primaryText={beneficiaryNumberType.value}
                    value={beneficiaryNumberType.uid}
                    key={beneficiaryNumberType.uid}
                    style={styles.fontText}
                  />
                ))
              }
            </SelectField>
          </div>

          <div className='col-md-6 col-xs-12'>
            <TextField
              name='number'
              floatingLabelText={this.props.intl.formatMessage(constants.messages.STEP_THREE_BENEFICIARY_NUMBER)}
              value={R.path(['beneficiary_numbers', 0, 'number'], prestation)}
              meta={{
                'mandatory': mandatory_fields.includes('beneficiary_numbers')
              }}
              placeholderMandatory={mandatory_fields.includes('beneficiary_numbers')}
              onChange={this.handleBeneficiaryNumberChange}
              errorText={R.path(['errors', 'beneficiary_numbers.number'], prestation)}
              floatingLabelStyle={styles.fontText}
            />
          </div>
        </div>

        <div className='row'>
          <div
            className={
              this.props.user.initialBeneficiaryNumber && prestation.id
                ? 'col-md-6 col-xs-6'
                : 'col-md-12 col-xs-12'
            }
          >
            <TextField
              meta={meta.other_beneficiaries}
              name='other_beneficiaries'
              value={prestation.other_beneficiaries || undefined}
              onChange={this.handlePrestationChange}
              floatingLabelText={this.props.intl.formatMessage(constants.messages.STEP_THREE_OTHER_BENEFICIARIES)}
              floatingLabelStyle={styles.fontText}
            />
          </div>

          {
            this.props.user.initialBeneficiaryNumber && prestation.id
              ? <div className='col-md-6 col-xs-6'>
                <TextField
                  name='admin_number'
                  type='text'
                  floatingLabelText={this.props.intl.formatMessage(constants.messages.INITIAL_BENEFICIARY_NUMBER)}
                  floatingLabelStyle={styles.fontText}
                  value={R.path(['beneficiary_numbers', 0, 'admin_number'], prestation)}
                  onChange={this.handleBeneficiaryNumberChange}
                  errorText={R.path(['errors', 'beneficiary_numbers.admin_number'], prestation)}
                />
              </div>
              : undefined
          }
        </div>

        {
          !R.path(['invoice_only_additional_services'], prestation)
            ? <div className='row'>
              <div className='col-md-12 col-xs-12'>
                <TextField
                  rows={2}
                  rowsMax={2}
                  multiLine={true}
                  meta={meta.additional_address_line}
                  name='additional_address_line'
                  value={prestation.additional_address_line || undefined}
                  onChange={this.handlePrestationChange}
                  floatingLabelText={this.props.intl.formatMessage(constants.messages.STEP_THREE_ADDITIONAL_ADDRESS_LINE)}
                  floatingLabelStyle={styles.fontText}
                />
              </div>
            </div>
            : undefined
        }

        <div className='row'>
          <div className='col-md-12 col-xs-12'>
            <TextField
              name='reason'
              floatingLabelText={this.props.intl.formatMessage(constants.messages.STEP_THREE_REASONS)}
              value={prestation.reason || undefined}
              meta={{
                'mandatory': mandatory_fields.includes('reason')
              }}
              placeholderMandatory={mandatory_fields.includes('reason')}
              multiLine={true}
              rows={2}
              rowsMax={2}
              onChange={this.handlePrestationChange}
              floatingLabelStyle={styles.fontText}
              errorText={R.path(['errors', 'reason'], prestation)}
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-md-12 col-xs-12'>
            <TextField
              meta={meta.remarks}
              multiLine={true}
              rows={2}
              rowsMax={2}
              floatingLabelText={this.props.intl.formatMessage(constants.messages.STEP_THREE_REMARKS)}
              name='remarks'
              value={prestation.remarks || undefined}
              onChange={this.handlePrestationChange}
              floatingLabelStyle={styles.fontText}
            />
          </div>
        </div>

        <div
          className='row'
        >
          <div className='col-md-offset-1 col-md-5 col-xs-6'>
            <RaisedButton
              style={styles.button}
              labelStyle={{fontSize: 13}}
              label={this.props.intl.formatMessage(constants.messages.CANCEL_BUTTON)}
              onClick={this.handleCancel}
              secondary={true}
            />
          </div>

          <div className='col-md-5 col-xs-6'>
            <RaisedButton
              primary={true}
              style={styles.button}
              labelStyle={{fontSize: 13}}
              disabled={
                (!prestation.uid &&
                 prestation.interpreters.filter(i => i.selected === false).length === prestation.interpreters.length) ||
                !this.props.submitPrestation
              }
              label={
                prestation.uid
                ? this.props.intl.formatMessage(constants.messages.VALIDATE_EDIT_BUTTON)
                : this.props.intl.formatMessage(constants.messages.VALIDATE_BUTTON)
              }
              onClick={this.handleConfirm}
            />
          </div>
        </div>
			</Card>
    )
  }
}

const styles = {
  card: {
    paddingLeft: 5,
    paddingRight: 5,
    paddingBottom: 5,
    height: '100%'
  },
  fontText: {
    fontSize: 14
  },
  button: {
    width: '100%',
    overflow: 'hidden'
  },
}

const mapStateToProps = (state) => ({
  beneficiaryNumberTypes: state.ApplicantPrestation.beneficiaryNumberTypes,
  user: state.User.user,
})

const mapActionsToProps = (dispatch) => ({
  headerActions: bindActionCreators(headerActions, dispatch),
})

export default withRouter(
  connect(mapStateToProps, mapActionsToProps)
  (injectIntl(PrestationInformation))
)



// WEBPACK FOOTER //
// ./src/components/applicant/prestation/creation/PrestationInformation.js